// ============================================
// Patient Journey Firestore Service
//
// Persists the patient's session journey so they can
// close their browser and come back to where they were.
//
// Collection: patient_journeys/{sessionId}
// Subcollection: patient_journeys/{sessionId}/messages/{auto}
// ============================================

import {
  doc, setDoc, getDoc, collection, addDoc,
  query, orderBy, getDocs, Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { TriageData, ChatMessage, AppScreen } from '../../types';
import {
  C_PATIENT_JOURNEYS,
  SC_MESSAGES,
} from '../../constants/collections';
import type { JourneyStepKey } from '../../config/journeyConfig';
import { getStepLabel, getScreenToStep } from '../../config/journeyConfig';

// Re-export so consumers can get display names through the journey service
export { getStepLabel };

// ── Journey step type (derived from journeyConfig — single source of truth) ──

export type JourneyStep = JourneyStepKey;

export type JourneyStatus = 'active' | 'complete' | 'abandoned';

// ── Journey document shape ──

export interface PatientJourney {
  sessionId: string;
  uid: string | null;
  email?: string;
  triageData: TriageData | null;
  currentStep: JourneyStep;
  currentScreen: AppScreen;
  labReportId: string | null;
  protocolId: string | null;
  createdAt: string;
  lastActivityAt: string;
  status: JourneyStatus;
}

// ── Collection refs ──

const COLLECTION = C_PATIENT_JOURNEYS;

const journeyDoc = (sessionId: string) => doc(db, COLLECTION, sessionId);
const messagesCol = (sessionId: string) => collection(db, COLLECTION, sessionId, SC_MESSAGES);

// ── Map screen → journey step (config-driven, Fix 8) ──

export function screenToStep(screen: AppScreen): JourneyStep {
  return getScreenToStep(screen);
}

// ── CRUD ──

/** Create a new journey when triage completes. Fire-and-forget. */
export async function createJourney(
  sessionId: string,
  triageData: TriageData,
  uid: string | null,
): Promise<void> {
  const now = new Date().toISOString();

  await setDoc(journeyDoc(sessionId), stripUndefined({
    sessionId,
    uid,
    triageData,
    currentStep: 'chat' as JourneyStep,
    currentScreen: 'chat' as AppScreen,
    labReportId: null,
    protocolId: null,
    createdAt: now,
    lastActivityAt: now,
    status: 'active' as JourneyStatus,
    _createdAt: Timestamp.now(),
    _updatedAt: Timestamp.now(),
  }));
}

/** Update journey step + screen. Fire-and-forget. Uses merge to tolerate race with createJourney. */
export async function updateJourneyStep(
  sessionId: string,
  screen: AppScreen,
  updates?: Partial<Pick<PatientJourney, 'labReportId' | 'protocolId' | 'status'>>,
): Promise<void> {
  await setDoc(journeyDoc(sessionId), stripUndefined({
    ...updates,
    currentStep: screenToStep(screen),
    currentScreen: screen,
    lastActivityAt: new Date().toISOString(),
    _updatedAt: Timestamp.now(),
  }), { merge: true });
}

/** Link journey to authenticated UID after account creation or sign-in. Fire-and-forget. */
export async function linkJourneyToUid(
  sessionId: string,
  uid: string,
  orderEmail?: string,
  protocolHash?: string,
): Promise<void> {
  try {
    await setDoc(journeyDoc(sessionId), {
      uid,
      _updatedAt: Timestamp.now(),
    }, { merge: true });
  } catch (primaryError) {
    const primaryErrMsg = primaryError instanceof Error ? primaryError.message : String(primaryError);
    console.error('[linkJourneyToUid] Primary write failed — escalating to failure handler:', primaryErrMsg);
    // Invoke server-side failure cascade: failed_journey_links, Cloud Logging, GCS, PagerDuty
    try {
      const reportFailure = httpsCallable(functions, 'reportJourneyLinkFailure');
      await reportFailure({ sessionId, uid, primaryError: primaryErrMsg, orderEmail, protocolHash });
    } catch (handlerError) {
      // Log but do not suppress — caller must still receive the error
      console.error('[linkJourneyToUid] Failure handler call itself failed:', handlerError);
    }
    throw primaryError;
  }
}

/** Mark journey complete. */
export async function completeJourney(sessionId: string): Promise<void> {
  await setDoc(journeyDoc(sessionId), {
    status: 'complete' as JourneyStatus,
    currentStep: 'complete' as JourneyStep,
    lastActivityAt: new Date().toISOString(),
    _updatedAt: Timestamp.now(),
  }, { merge: true });
}

/** Retrieve a journey by sessionId. Returns null if none exists. */
export async function getJourney(sessionId: string): Promise<PatientJourney | null> {
  const snap = await getDoc(journeyDoc(sessionId));
  return snap.exists() ? (snap.data() as PatientJourney) : null;
}

// ── Messages subcollection ──

/** Save a single chat message. Fire-and-forget. */
export async function saveMessage(sessionId: string, message: ChatMessage): Promise<void> {
  await addDoc(messagesCol(sessionId), stripUndefined({
    ...message,
    _savedAt: Timestamp.now(),
  }));
}

/** Load all messages for a journey, ordered by timestamp. */
export async function loadMessages(sessionId: string): Promise<ChatMessage[]> {
  const q = query(messagesCol(sessionId), orderBy('timestamp', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: data.id as string,
      role: data.role as ChatMessage['role'],
      content: data.content as string,
      timestamp: data.timestamp as string,
      widget: data.widget as ChatMessage['widget'],
      metadata: data.metadata as ChatMessage['metadata'],
    };
  });
}

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
